import { useState, useEffect } from 'react';
import Layout from '../../components/layout';
import { useAsync } from 'react-async';
import { TimesheetStatus } from '../../common/constants';
import { sortAlphabetically, generateOptions } from '../../common/utils';
import React from 'react';
import DataTable from '../../components/dataTable';
import SubmitButton from '../../components/submitButton';
import { getTargetDisciplines } from '../../api/services/disciplineService';
import { getDistrictOptions } from '../../api/services/districtService';
import { getSchoolsByDistrict } from '../../api/services/schoolService';
import { searchTimesheetEntrySummaries } from '../../api/services/timesheetService';
import { batchTransferDisciplinePrograms } from '../../api/services/timesheetService';
import { getDivisionOptions } from '../../api/services/divisionService';
import { getDistrictsByDivisionId, getTherapistListByName, getDisciplinesByTherapistId } from '../../api/services/therapistService';
import { TimesheetEntrySummarySearch } from '../../api/models/TimesheetEntrySummarySearch';
import { getProgramsByDistrict, getTargetPrograms } from '../../api/services/programService';
import Select from 'react-select';
import { option } from '../../api/models/option';
import DatePicker from 'react-datepicker';
import CustomAlert from '../../components/alert/commonAlert';
import { EntryTypes } from '../../common/constants';
import { mapToOptions } from '../../common/utils';
import { Notification } from '../../components/notifications';


interface TimeEntriesWithBillingPeriodRequest {
    TimesheetEntryIds: string;
    BillingPeriods: string;
}
const entryTypeOptions = Object.keys(EntryTypes)
    .filter((key) => isNaN(Number(key))) // Exclude numeric reverse mapping
    .map((key) => ({
        label: key == "NonStudent" ? "Non-student" : key, // Will show "Student", "NonStudent", etc.
        value: EntryTypes[key as keyof typeof EntryTypes],
    }));
const fetchDistricts = async () => getDistrictOptions();
const fetchDivisions = async () => getDivisionOptions();

// Entry Summary Search
const doSearch = async (args: any[]) => {
    return searchTimesheetEntrySummaries(args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7], args[8], args[9], args[10]);
}

// Batch Transfer
const doBatchTransfer = async (args: any[]) => {
    return batchTransferDisciplinePrograms(args[0], args[1], args[2], args[3], args[4], args[5]);
};

const getCurrentWeekDates = () => {

    const today = new Date();
    const currentDate = today.getDate();
    const currentDay = today.getDay();
    const mondayDate = new Date(today);
    mondayDate.setDate(currentDate - (currentDay - 1));
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);

    return {
        monday: mondayDate,
        sunday: sundayDate,
    };
};

interface TherapistOption {
    label: string;
    value: string | number;
}

/* Component */
const TimeEntryTransfer = () => {

    const [selectedEntries, setSelectedEntries] = useState<TimeEntriesWithBillingPeriodRequest[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);


    const columns = React.useMemo(
        () => [
            {
                id: 'select',
                accessor: 'TimesheetEntryIds',
                width: '30px',
                className: 'block mr-4 mt-2',
                disableSortBy: true,
                autoResetSortBy: false,
                Header: () => <></>,
                Cell: ({ row }: any) => {
                    const t: TimesheetEntrySummarySearch = row.original;
                    const timesheetId = String(t.timesheetEntryIds ?? '');
                    const billingPeriod = String(t.billingPeriod ?? '');

                    const isChecked = selectedEntries.some(
                        entry =>
                            entry.TimesheetEntryIds === timesheetId &&
                            entry.BillingPeriods === billingPeriod
                    );

                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const isSelected = e.target.checked;

                        setSelectedEntries(prev => {
                            if (isSelected) {
                                const exists = prev.some(
                                    entry =>
                                        entry.TimesheetEntryIds === timesheetId &&
                                        entry.BillingPeriods === billingPeriod
                                );
                                return exists ? prev : [...prev, { TimesheetEntryIds: timesheetId, BillingPeriods: billingPeriod }];
                            } else {
                                return prev.filter(
                                    entry =>
                                        !(
                                            entry.TimesheetEntryIds === timesheetId &&
                                            entry.BillingPeriods === billingPeriod
                                        )
                                );
                            }
                        });

                        e.stopPropagation();
                    };
                    const isDisabled = row.original.rowEnabled === 0;
                    return (
                        <input
                            id={`entry_${timesheetId}_${billingPeriod}`}
                            className="datatable-checkbox"
                            type="checkbox"
                            disabled={isDisabled}
                            data-row-index={row.index}
                            checked={isChecked}
                            onChange={handleChange}
                        />
                    );
                }
            },
            {
                Header: 'Billing Period',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.billingPeriod;
                }
            },
            {
                Header: 'Timesheet Status Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.timesheetStatusName;
                },
            },
            {
                Header: 'Student Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.studentName;
                },
            },
            {
                Header: 'Student ID',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.studentId;
                },
            },
            {
                Header: 'Division Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.divisionName;
                },
            },
            {
                Header: 'District Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.districtName;
                },
            },
            {
                Header: 'Program Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.programName;
                },
            },
            {
                Header: 'School Name',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.schoolName;
                },
            },
            {
                Header: 'Date Of Entry',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.dateOfEntry;
                },
            },
            {
                Header: 'Entry Type',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.entryTypeName;
                },
            },
            {
                Header: 'Task Type',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return t.taskTypeName;
                },
            },
            {
                Header: 'Minutes',
                accessor: (s: any) => {
                    const t: TimesheetEntrySummarySearch = s;
                    return `${t.totalMinutes}`;
                },
            }
        ],
        [selectedEntries]);

    //startDate and endDate (required)
    const { monday, sunday } = getCurrentWeekDates();
    const [startDate, setStartDate] = useState<Date | null>(monday);
    const [endDate, setEndDate] = useState<Date | null>(sunday);
    const [therapistId, setTherapistId] = useState<number>();

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string>();
    const [message, setMessage] = useState<string>();
    const [isSuccess, setIsSuccess] = useState<boolean>(false)


    //Timesheet hooks  
    const [disciplineOptions, setDisciplineOptions] = useState<option[]>([]);
    const [discipline, setDiscipline] = useState<number>(-1);
    const [entryTypes, setEntryTypes] = useState<string[]>([]);
    const [targetDisciplineId, setTargetDisciplineId] = useState<number>(-1);
    const [schoolOptions, setSchoolOptions] = useState<option[]>([]);
    const [school, setSchool] = useState<number>(-1);
    const [districtOptions, setDistrictOptions] = useState<option[]>([]);
    const [districtId, setDistrictId] = useState<number>(-1);
    const [programId, setProgramId] = useState<number>(-1);
    const [targetProgramId, setTargetProgramId] = useState<number>(-1);

    const [divisionOptions, setDivisionOptions] = useState<option[]>([]);
    const [programOptions, setProgramOptions] = useState<option[]>([]);
    const [targetProgramOptions, setTargetProgramOptions] = useState<option[]>([]);
    const [targetDisciplineOptions, setTargetDisciplineOptions] = useState<option[]>([]);
    const [division, setDivision] = useState<number>(-1);
    const [searchResults, setSearchResults] = useState<TimesheetEntrySummarySearch[]>([]);
    const [isSelect, setIsSelect] = useState<boolean>(false);
    const [selectedEntryTypes, setSelectedEntryTypes] = useState<{ label: string; value: number }[]>([]);
    const [therapistList, setTherapistList] = useState<TherapistOption[]>([]);
    const [selectedTherapist, setSelectedTherapist] = useState<TherapistOption | null>(null);

    const districtsData = useAsync({
        promiseFn: fetchDistricts,
        onResolve: (u) => {
            let options: option[] = districtOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            setDistrictOptions(options);
        },
    });

    const divisionAsync = useAsync({
        promiseFn: fetchDivisions,
        onResolve: (u) => {
            let options: option[] = divisionOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            options.shift();
            setDivisionOptions(options);
        },
    });

    /**
     * Whenever district changes, update the list of schools
     */
    useEffect(() => {
        updatePrograms(districtId, division);

        if (districtId != null) {
            updateSchools(districtId);
        }
    }, [districtId]);

    /**
    * Functions to update the select boxes
    */
    const updateSchools = async (district: number) => {
        const response = await getSchoolsByDistrict(district);

        //If we don't have any schools, inform the user
        if (response.data.schools.length === 0) {
            setSchoolOptions([{ value: -1, label: '' }]);
            return true;
        }

        const schools = generateOptions(response.data.schools, 'schoolId', 'name', true);
        setSchoolOptions(schools);
    }

    const updatePrograms = async (district: number, division: number) => {
        const response = await getProgramsByDistrict(district, division);

        if (response.data.programs.length === 0) {
            setProgramOptions([{ value: -1, label: '' }]);
            return true;
        }

        const programs = generateOptions(response.data.programs, 'programId', 'name', true);
        setProgramOptions(programs);
    }

    /**
     * Whenever district changes, update the list of schools
     */
    useEffect(() => {
        if (division > 0) updateDistricts(division);

        updatePrograms(districtId, division);
    }, [division]);


    /**
    * Functions to update the select boxes
    */
    const updateDistricts = async (divisionId: number) => {
        const response = await getDistrictsByDivisionId(divisionId);

        //If we don't have any schools, inform the user
        if (response.data.districts.length === 0) {
            setDistrictOptions([{ value: -1, label: '' }]);
            return true;
        }

        const districts = generateOptions(response.data.districts, 'districtId', 'name', true);
        setDistrictOptions(districts);
    }
    // Transfer button handler
    const handleTransferClick = async () => {
        if (!therapistId || selectedEntries.length === 0) {
            setIsSuccess(false);
            setMessage('Please select therapist and entries.');
            return;
        }

        // Group by BillingPeriods
        const grouped = new Map<string, string[]>();
        selectedEntries.forEach(({ TimesheetEntryIds, BillingPeriods }) => {
            if (!grouped.has(BillingPeriods)) grouped.set(BillingPeriods, []);
            grouped.get(BillingPeriods)!.push(TimesheetEntryIds);
        });

        const payload: TimeEntriesWithBillingPeriodRequest[] = Array.from(grouped).map(
            ([BillingPeriods, ids]) => ({
                TimesheetEntryIds: ids.join(','),
                BillingPeriods
            })
        );
        const args = [
            payload,
            therapistId,
            discipline,
            programId,
            targetDisciplineId,
            targetProgramId
        ];
        setIsSubmitting(true);
        try {
            const result = await doBatchTransfer(args);
            if (result?.data?.success) {
                const updatedSearchResults = searchResults.filter(result =>
                    !selectedEntries.some(entry =>
                        entry.TimesheetEntryIds === result.timesheetEntryIds &&
                        entry.BillingPeriods === result.billingPeriod
                    )
                );
                setSearchResults(updatedSearchResults);
                setIsSuccess(true);
                setMessage(result?.data?.message);
                setSelectedEntries([]);
            }
            else {
                setIsSuccess(false);
                setMessage(result?.data?.message);
            }
        } catch (err) {
            setIsSuccess(false);
            setMessage("Transfer Failed!");
        } finally {
            setIsSubmitting(false);
        }
    };
    /**
     * Primary search async function
     */
    const handleSearchAsync = useAsync({
        deferFn: doSearch,
        onResolve: (result) => {
            if (result.error) {
                setSearchResults([]);
                setMessage(result.error);
            } else {
                setSearchResults(result.data || []);
                setMessage('');
            }
        },
        onReject: (err) => {
            console.error('Unhandled error:', err);
            setSearchResults([]);
            setMessage(typeof err === 'string' ? err : 'Unexpected error occurred.');
        },
    });


    const resetFilter = () => {
        setTherapistId(-1);
        const dates = getCurrentWeekDates();
        setStartDate(dates.monday);
        setEndDate(dates.sunday);
        setTargetDisciplineId(-1);
        setDistrictId(-1);
        setSchool(-1);
        setDiscipline(-1);
        setTargetProgramId(-1);
        setDivision(-1);
        setProgramId(-1);
        setEntryTypes([]);
        setSelectedEntryTypes([]);
        setSelectedTherapist(null);
        setTherapistList([]);
        setTargetDisciplineOptions([]);
        setTargetProgramOptions([]);
        setMessage('');
        setIsSuccess(false);
    }

    /**
     * This function selects or de-selects all checkboxes in a datatable
     *
     */
    const toggleSelectAll = (): void => {
        const checkboxes = [...document.querySelectorAll('.datatable-checkbox')];

        if (isSelect) {
            // Unselect All
            checkboxes.forEach((box) =>
                setTimeout(() => ((box as HTMLInputElement).checked = false), 0)
            );
            setSelectedEntries([]);
            setIsSelect(false);
        } else {
            // Select All (Only enabled)
            const enabledIds: TimeEntriesWithBillingPeriodRequest[] = [];

            checkboxes.forEach((box: any) => {
                const rowIndex = Number(box.getAttribute('data-row-index'));
                const row = searchResults[rowIndex];
                const isEnabled = row?.rowEnabled === 1;

                if (isEnabled) {
                    enabledIds.push({ TimesheetEntryIds: row.timesheetEntryIds, BillingPeriods: row.billingPeriod });
                    setTimeout(() => ((box as HTMLInputElement).checked = true), 0);
                }
            });

            setSelectedEntries(enabledIds);
            setIsSelect(true);
        }
    };


    /**
     * JSX element for below the filter element on a datatable
     */
    const transferBar = <div className="pb-0.5 mt-4 mb-0.5 approval-bar">
        <button
            onClick={toggleSelectAll}
            className='inline-flex justify-center px-4 py-2 text-base text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsblue-normal hover:bg-ptsblue-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
        >{isSelect ? 'Unselect All' : 'Select All'}</button>
        <button
            type="button"
            onClick={handleTransferClick}
            disabled={isSubmitting || selectedEntries.length === 0}
            className='inline-flex justify-center px-4 py-2 text-base text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsgreen-normal hover:bg-ptsgreen-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
        >Transfer</button>
    </div>;

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const handleTherapistListChange = async (inputValue: string) => {
        if (inputValue.length < 3) {
            setTherapistList([]); // Clear if less than 3 chars
            return;
        }

        try {

            const response = await getTherapistListByName(inputValue);
            const data = response.data;
            const mappedOptions: TherapistOption[] = data.map((item: any) => ({
                label: item.userName,
                value: item.userId,
            }));

            setTherapistList(mappedOptions);
        } catch (error) {
            setTherapistList([]);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {

                const response = await getDisciplinesByTherapistId(therapistId!);
                const data = response.data;
                const mappedDisciplines = mapToOptions(data?.disciplines, 'disciplineId', 'name');

                setDisciplineOptions(mappedDisciplines as any);

            } catch (error) {
                setDisciplineOptions([]);
            }

        };

        if (therapistId) {
            fetchData();
        }
    }, [therapistId]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                if (discipline != -1) {
                    const [targetDisciplines, targetPrograms] = await Promise.all([
                        getTargetDisciplines(startDate!, endDate!, therapistId!, discipline!),
                        getTargetPrograms(startDate!, endDate!, therapistId!, discipline!),
                    ]);

                    const mappedTargetDisciplines = mapToOptions(targetDisciplines?.data, 'disciplineId', 'name');
                    const mappedTargetPrograms = mapToOptions(targetPrograms?.data, 'programId', 'name');

                    // Set the options after all responses are received
                    setTargetDisciplineOptions(mappedTargetDisciplines as any);
                    setTargetProgramOptions(mappedTargetPrograms as any);
                }
            } catch (error) {
                setTargetDisciplineOptions([]);
                setTargetProgramOptions([]);
            }
        };

        if (discipline) {
            fetchData();
        }
    }, [discipline]);

    const closeErrors = () => {
        setMessage('');
    };

    return (
        <Layout
            isLoading={
                districtsData.isPending ||
                divisionAsync.isPending ||
                handleSearchAsync.isPending
            }>
            <div>
                <div className='w-full mt-2'>

                    {message && <Notification isSuccess={isSuccess} onClose={closeErrors} message={message} />}

                    <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                        <div className='z-0 grid grid-cols-6 gap-6 mb-4'>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                    <label className='primary'>Therapist<span className="text-red-500">*</span></label>
                                    <Select
                                        className="block mt-1 primary"
                                        name="therapist"
                                        options={therapistList}
                                        placeholder="Select therapist"
                                        isSearchable={true}
                                        value={selectedTherapist}
                                        onInputChange={(inputValue, { action }) => {
                                            const isTypedInput = action === 'input-change';
                                            const isValidSearch = /^[a-zA-Z]{3,}$/.test(inputValue);
                                            if (isTypedInput && isValidSearch) {
                                                setTherapistList([]);
                                                handleTherapistListChange(inputValue);
                                            }

                                            return inputValue;
                                        }}
                                        onChange={(selectedOption) => {
                                            console.log('Selected therapist:', selectedOption);
                                            setSelectedTherapist(selectedOption as any)
                                            setTherapistId(selectedOption?.value as number);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Start Date<span className="text-red-500">*</span></label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        if (date) {
                                            const maxEndDate = new Date(date as Date);
                                            maxEndDate.setDate(maxEndDate.getDate() + 30); // Set max end date to 31 days after new start date

                                            // Check if current end date is within the new valid range
                                            if (endDate && endDate > maxEndDate) {
                                                setAlertMessage("The selected end date exceeds the 31-day limit from the new start date. Please choose a valid end date.");
                                                setShowAlert(true)
                                                setEndDate(null);
                                            }
                                        }
                                        setTargetDisciplineOptions([]);
                                        setTargetProgramOptions([]);
                                        setTargetProgramId(-1);
                                        setTargetDisciplineId(-1);
                                        setDiscipline(-1);
                                        setStartDate(date as Date)
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    maxDate={endDate}
                                    endDate={endDate}
                                    className="border p-2 rounded"
                                    placeholderText="Select a start date"
                                />

                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>End Date<span className="text-red-500">*</span></label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => {
                                        if (startDate && date) {
                                            const maxEndDate = new Date(startDate);
                                            maxEndDate.setDate(maxEndDate.getDate() + 30); // Add 30 days to start date
                                            if (date > maxEndDate) {
                                                setAlertMessage("End date cannot exceed 31 days from the start date.");
                                                setShowAlert(true);
                                                return;
                                            }
                                        }

                                        setTargetDisciplineOptions([]);
                                        setTargetProgramOptions([]);
                                        setTargetProgramId(-1);
                                        setTargetDisciplineId(-1);
                                        setDiscipline(-1);
                                        setEndDate(date as Date)
                                    }}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    className="border p-2 rounded"
                                    placeholderText="Select an end date"
                                />
                            </div>
                        </div>
                        {showAlert && (
                            <CustomAlert
                                message={alertMessage!}
                                onClose={handleCloseAlert}
                            />
                        )}
                        <div className='z-0 grid grid-cols-6 gap-6'>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Discipline<span className="text-red-500">*</span></label>
                                <Select
                                    className='block mt-1 primary'
                                    value={{ label: disciplineOptions.find(x => x.value === discipline)?.label, value: discipline } as option}
                                    name='test'
                                    options={disciplineOptions ?? []}
                                    placeholder={`Select Discipline`}
                                    onChange={(e: any | null) => {
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setDiscipline(Number(e.value));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Entry Type</label>
                                <Select
                                    className='block mt-1 primary'
                                    isMulti
                                    value={selectedEntryTypes}
                                    name='entryTypes'
                                    options={entryTypeOptions}
                                    placeholder="Select Entry Types"
                                    onChange={(selectedOptions) => {
                                        setEntryTypes(Array.from(selectedOptions).map(type => type.label));
                                        setSelectedEntryTypes(Array.from(selectedOptions ?? []));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>

                                <label className='primary'>District</label>
                                <Select
                                    className='block mt-1 primary'
                                    value={{ label: districtOptions.find(x => x.value === districtId)?.label, value: districtId } as option}
                                    name='test'
                                    options={districtOptions ?? []}
                                    placeholder={`Select District`}
                                    onChange={(e: any | null) => {
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setDistrictId(Number(e.value));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Division</label>
                                <Select
                                    className='block mt-1 primary'
                                    value={{ label: divisionOptions.find(x => x.value === division)?.label, value: division } as option}
                                    name='test'
                                    options={divisionOptions ?? []}
                                    placeholder={`Select Division`}
                                    onChange={(e: any | null) => {
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setDivision(Number(e.value));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Program</label>
                                <Select
                                    className='block mt-1 primary'
                                    value={{ label: programOptions.find(x => x.value === programId)?.label, value: programId } as option}
                                    name='test'
                                    options={programOptions ?? []}
                                    placeholder={`Select Program`}
                                    onChange={(e: any | null) => {
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setProgramId(Number(e.value));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>

                                <label className='primary'>School</label>
                                <Select
                                    className='block mt-1 primary'
                                    value={{ label: schoolOptions.find(x => x.value === school)?.label, value: school } as option}
                                    name='test'
                                    options={schoolOptions ?? []}
                                    placeholder={`Select School`}
                                    onChange={(e: any | null) => {
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setSchool(Number(e.value));
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            {/* Target Section Separator */}
                            <div className="col-span-6 mt-6">
                                <div className="relative border border-gray-300 rounded-md px-4 pt-4 pb-2">
                                    <span className="absolute -top-3 left-4 bg-white px-2 text-sm text-gray-700 font-medium">
                                        Target Section
                                    </span>
                                    <div className="grid grid-cols-6 gap-6">
                                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                            <label className='primary'>Target Discipline</label>
                                            <Select
                                                className='block mt-1 primary'
                                                value={targetDisciplineOptions.find(option => option.value === targetDisciplineId) || null}
                                                name='target-discipline'
                                                options={targetDisciplineOptions ?? []}
                                                placeholder={`Select Discipline`}
                                                onChange={(e: any | null) => {
                                                    e = e !== null ? e : { label: '', value: -1 };
                                                    setTargetDisciplineId(Number(e.value));
                                                    setTargetProgramId(-1);
                                                }}
                                                isSearchable={false}
                                            />
                                        </div>
                                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                            <label className='primary'>Target Program</label>
                                            <Select
                                                className='block mt-1 primary'
                                                value={targetProgramOptions.find(option => option.value === targetProgramId) || null}
                                                name='target-program'
                                                options={targetProgramOptions ?? []}
                                                placeholder={`Select Program`}
                                                onChange={(e: any | null) => {
                                                    e = e !== null ? e : { label: '', value: -1 };
                                                    setTargetProgramId(Number(e.value));
                                                    setTargetDisciplineId(-1);
                                                }}
                                                isSearchable={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
                        <SubmitButton
                            text='Search'
                            className='mr-5 primary button'
                            onClick={() => handleSearchAsync.run(startDate, endDate, therapistId, districtId, school, discipline, targetProgramId, division, targetDisciplineId, programId, entryTypes)}
                            disabled={
                                startDate == undefined || startDate == null ||
                                endDate == undefined || endDate == null ||
                                discipline == targetDisciplineId || !(targetDisciplineId !== -1 || targetProgramId !== -1)
                            }
                        />

                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <button
                                className='mr-5 primary button'
                                onClick={() => { resetFilter(); }}
                                style={{ backgroundColor: '#005782' }}
                            >
                                Reset filter
                            </button>
                        </div>
                    </div>
                    <div className='p-3 mt-5 bg-white rounded-lg shadow'>
                        <div className='mt-5'></div>
                        <DataTable
                            columns={columns}
                            data={searchResults}
                            showPagination={true}
                            postFilter={transferBar}
                            getRowProps={(row: any) => ({
                                className: row.original.rowEnabled === 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'cursor-pointer hover:bg-blue-100',
                                onClick: () => {
                                    if (row.original.rowEnabled === 0) return;
                                    // click on row -> logic here if needed
                                }
                            })}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default TimeEntryTransfer;

